import Konva from 'konva';
import { KONVA_FACET_GROUP, KONVA_OBS_GROUP, PANEL_OFF_COLOR , FINAL_IMAGE_PADDING_VALUE_PERCENTAGE, PANEL_ON_COLOR } from '../../../constants';

// Mapping of groupnames
const KonvaGroupNames = {
	/**Groups**/
	existingFacetsGroup: KONVA_FACET_GROUP.EXISTING_GROUP_NAME,
	existingPanelsGroup: `${KONVA_FACET_GROUP.EXISTING_GROUP_NAME}${KONVA_FACET_GROUP.GROUP_NAME_SEPARATOR}${KONVA_FACET_GROUP.PANEL_GROUP_SUFFIX}`,
	obstructionsGroup: 'obstructions',
	existingFacetsSetbackAreaGroup: 'setback-area',
	inProgressFacetAzimuthSelectorGroup: `${KONVA_FACET_GROUP.IN_PROGRESS_GROUP_NAME}${KONVA_FACET_GROUP.GROUP_NAME_SEPARATOR}${KONVA_FACET_GROUP.AZIMUTH_SELECTOR_SUFFIX}`,
	facetDrawingGroup: 'panel-drawer',
	inProgressUserDefinedFacetGroup: KONVA_FACET_GROUP.IN_PROGRESS_GROUP_NAME,
	inProgressUserDefinedObsGroup: KONVA_OBS_GROUP.IN_PROGRESS_GROUP_NAME,
	obstructionDrawingGroup: 'manual-obstruction',
	EDGE_LENGHT: 'edge-length',

	/**Lines**/
	existingFacet: `${KONVA_FACET_GROUP.EXISTING_GROUP_NAME}${KONVA_FACET_GROUP.GROUP_NAME_SEPARATOR}facet`,

	/** Maps */
	mapGroup: 'map-group',
	customMapGroup: 'custom-maps-group'
};

const KonvaClassNames = {
	solarFeetLineText: 'solar-feet-lines',
	inProgressLineFeetText: 'preview-line-text'
};

const KonvaLayerIds = {
	labelLayerId: 'label-layer',
	baseLayerId: 'base-layer',

};

const konvaImageIds = {
	bgImage: 'bf-image',
	irradianceImageGroup: 'irradiance-image-group'
};

function getBaseRasterImage(stage: Konva.Stage): Konva.Image | undefined {
	return stage.findOne(`#${konvaImageIds.bgImage}`) as Konva.Image;
}

function isBaseRasterImageLoaded(stage: Konva.Stage): boolean {
	return !!getBaseRasterImage(stage)?.image();
}

// Resolved by RasterImage.tsx via notifyBaseRasterImageLoaded() once `use-image`
// settles (onload/onerror), so callers below don't have to poll for it.
let baseRasterImageLoadResolvers: Array<() => void> = [];

function notifyBaseRasterImageLoaded(): void {
	const resolvers = baseRasterImageLoadResolvers;
	baseRasterImageLoadResolvers = [];
	resolvers.forEach((resolve) => resolve());
}

function waitForBaseRasterImage(stage: Konva.Stage): Promise<boolean> {
	if (isBaseRasterImageLoaded(stage)) return Promise.resolve(true);
	return new Promise((resolve) => {
		baseRasterImageLoadResolvers.push(() => resolve(isBaseRasterImageLoaded(stage)));
	});
}

// Generate the panel group ID for a given roof segment
function getExistingPanelsGroupID(roofSegmentID: string) {
	return `${roofSegmentID}${KONVA_FACET_GROUP.GROUP_NAME_SEPARATOR}${KonvaGroupNames.existingPanelsGroup}`;
}

function getExistingFacetsGroupID(roofSegmentID: string) {
	return `${roofSegmentID}${KONVA_FACET_GROUP.GROUP_NAME_SEPARATOR}${KONVA_FACET_GROUP.EXISTING_GROUP_NAME}`;
}

function getInProgressFacetsGroupID(newlyCreatedFacetID: string) {
	return `${newlyCreatedFacetID}${KONVA_FACET_GROUP.GROUP_NAME_SEPARATOR}${KonvaGroupNames.inProgressUserDefinedFacetGroup}`;
}

function getRoofSegmentIDFromGroupID(groupID: string) {
	console.log(groupID);
	return groupID?.split(KONVA_FACET_GROUP.GROUP_NAME_SEPARATOR)[0];
}

function getObsIDFromGroupID(groupID: string) {
	console.log(groupID);
	return groupID?.split(KONVA_OBS_GROUP.GROUP_NAME_SEPARATOR)[0];
}

function getExistingFacetDisplayName(stage: Konva.Stage | null | undefined, facetGrpID: string) {
	if (!stage) return 'N/A';
	return stage.findOne(`#${facetGrpID}`)?.getAttr('displayName');
}

function findKonvaShapeByID(stage: Konva.Stage | null | undefined, facetGrpID: string) {
	if (!stage) return;
	return stage.findOne(`#${facetGrpID}`);
}

function checkIfAnyDrawnFacetExists(stage: Konva.Stage | null | undefined): boolean {
	if (!stage) return false;
	const panelDrawerGroup = stage.findOne(`.${KonvaGroupNames.facetDrawingGroup}`) as Konva.Group;
	if (!panelDrawerGroup?.hasChildren()) return false;

	const allInProcessGroups = panelDrawerGroup.find(`.${KonvaGroupNames.inProgressUserDefinedFacetGroup}`) as Konva.Group[];
	return allInProcessGroups.length > 0;
}

function checkIfAnyDrawnObstructionExists(stage: Konva.Stage | null | undefined): boolean {
	if (!stage) return false;
	const obsDrawerGroup = stage.findOne(`.${KonvaGroupNames.obstructionDrawingGroup}`) as Konva.Group;
	if (!obsDrawerGroup?.hasChildren()) return false;

	const allInProcessGroups = obsDrawerGroup.find(`.${KonvaGroupNames.inProgressUserDefinedObsGroup}`) as Konva.Group[];
	return allInProcessGroups.length > 0;
}

type BBOX = { x: number, y: number, width: number, height: number }
class RoofImageBuilder {

	private outputs: {
		baseImage?: string;
		labelsOnly?: string;
		filteredLabelsOnly?: string;
		solarLinerFtOnly?: string;
		panelsOnly?: string;
	} = {};

	private labelSelector = `.${KonvaGroupNames.existingFacet}-label`;
	private solarLinerFtSelector = `.${KonvaClassNames.solarFeetLineText}`;
	private panelsSelector = `.${KonvaGroupNames.existingPanelsGroup}`;
	private specialEdgesSelector = `.special-edges-${KONVA_FACET_GROUP.GROUP_NAME_SEPARATOR}${KONVA_FACET_GROUP.EXISTING_GROUP_NAME}`;
	private treeDrawerSelector = '.tree-drawer';
	private flattenAreaDrawerSelector = '.flatten-area-drawer';
	private dsmEditsFlattenAreaSelector = '.DSM_edits-flatten-area';
	private flattenAreaFillSelector = '.flatten-area-fill';

	private outputCfg: Konva.ContainerConfig =
		{
			mimeType: 'image/png',
			pixelRatio: 1
		};

	constructor(
		private attributes: {
			stage: Konva.Stage,
			fullScreenEnabled: boolean,
			groundMetersPerPixel: number,
			ogImgDims: { width: number, height: number },
			filteredRoofIds?: string[],
			houseBoundingBox?: HouseBoundingBox,
		}
	) {

		const { stage, ogImgDims, houseBoundingBox } = this.attributes;

		// Clone immediately and force it north-up first. getClientRect() below is measured in
		// absolute stage space, which bakes in the stage's own rotation — so every bbox measurement,
		// not just the final exported image, must run against an already-unrotated stage. The
		// roof-design save path never calls adjustScalingWhileSavingDesignImage, so this is the
		// only reset point it goes through; cloning first also means the live/original stage
		// (and its current rotate-mode view) is never touched.
		const exportStage = stage.clone({ listening: false, rotation: 0 });

		let scale = exportStage.scale();
		let position = exportStage.position();

		if (houseBoundingBox) {
			const img = exportStage.findOne('#' + konvaImageIds.bgImage) as Konva.Image;
			const absScale = img.getLayer()!.scale();
			const newBB = [
				houseBoundingBox[0] * absScale.x, houseBoundingBox[1] * absScale.y,
				houseBoundingBox[2] * absScale.x, houseBoundingBox[3] * absScale.y
			];

			// include custom map images in bbox
			let finalBB = newBB;

			const customMapGroup = exportStage.findOne(`.${KonvaGroupNames.customMapGroup}`);
			if (customMapGroup?.hasChildren()) {
				const rect = customMapGroup.getClientRect();
				const customBB: HouseBoundingBox = [
					rect.x,
					rect.y,
					rect.x + rect.width,
					rect.y + rect.height
				];

				finalBB = this.unionBBox(newBB, customBB);
			}

			const bbox = this.addBufferOnBBox(this.squarifyBoundingBox(finalBB), FINAL_IMAGE_PADDING_VALUE_PERCENTAGE);
			const normBBox = this.boundsToBBox(bbox);
			// Floor at native scale (1) to avoid blank edges exporting as black on JPEG.
			const newDim = this.zoomToBBox(normBBox, 0, 1);
			scale = { x: newDim.scale, y: newDim.scale };
			// imgExtent = real image size converted to the same units as newDim.position (absScale)
			position = this.clampPositionToImageBounds(
				newDim.scale,
				newDim.position,
				ogImgDims.width * absScale.x,
				ogImgDims.height * absScale.y
			);

		} else {
			// No bbox: keep the live view's centre and zoom, but north-up. The live position was worked
			// out for the rotated view, so reusing it with rotation 0 would frame the export wrong.
			// e.g. rotation 0 → centerOnMap maps back to the same position, so nothing changes.
			const screenCenter = { x: stage.width() / 2, y: stage.height() / 2 };
			const centerOnMap = stage.getAbsoluteTransform().copy().invert().point(screenCenter);
			position = {
				x: screenCenter.x - centerOnMap.x * scale.x,
				y: screenCenter.y - centerOnMap.y * scale.y,
			};
		}

		exportStage.scale(scale);
		exportStage.position(position);

		this.attributes.stage = exportStage;

	}
	// Keeps the bbox-derived crop window inside the real background image extent,
	// so a facet near a corner can't push the export past the image edge (black strip).
	private clampPositionToImageBounds(
		scale: number,
		position: { x: number; y: number },
		imgExtentW: number,
		imgExtentH: number
	) {
		const viewportW = this.attributes.stage.width();
		const viewportH = this.attributes.stage.height();

		const clampAxis = (pos: number, viewport: number, imgExtent: number) => {
			const minPos = viewport - imgExtent * scale;
			// image extent is smaller than the viewport at this scale: center it instead of clamping
			if (minPos > 0) return minPos / 2;
			return Math.min(0, Math.max(minPos, pos));
		};

		return {
			x: clampAxis(position.x, viewportW, imgExtentW),
			y: clampAxis(position.y, viewportH, imgExtentH),
		};
	}

	private unionBBox(a: HouseBoundingBox, b: HouseBoundingBox): HouseBoundingBox {
		return [
			Math.min(a[0], b[0]),
			Math.min(a[1], b[1]),
			Math.max(a[2], b[2]),
			Math.max(a[3], b[3])
		];
	}

	private boundsToBBox(bb: HouseBoundingBox): BBOX {
		const [minx, miny, maxx, maxy] = bb as HouseBoundingBox;
		return { x: minx, y: miny, width: maxx - minx, height: maxy - miny };
	}

	private squarifyBoundingBox(bbox: HouseBoundingBox): HouseBoundingBox {
		// eslint-disable-next-line prefer-const
		let [minx, miny, maxx, maxy] = bbox;
		const width = maxx - minx;
		const height = maxy - miny;

		if (width > height) maxy = miny + height;
		else maxx = minx + width;

		return [minx, miny, maxx, maxy];
	}

	private addBufferOnBBox(bbox: HouseBoundingBox, bufferPerc: number) {
		let [minx, miny, maxx, maxy] = bbox;
		const width = maxx - minx;

		// # 2. Apply a x % buffer to all sides of the bounding box.
		// # assming width = height because they are squarified
		const buffer = bufferPerc * width;
		minx -= buffer;
		miny -= buffer;
		maxx += buffer;
		maxy += buffer;

		return [minx, miny, maxx, maxy];
	}

	// move into hosues bbox
	private zoomToBBox(bbox: { x: number; y: number; width: number; height: number }, padding = 0, minScale = 0) {

		const viewportW = this.attributes.stage.width();
		const viewportH = this.attributes.stage.height();

		const scale = Math.max(
			minScale,
			0.05,
			Math.min(
				6,
				Math.min(
					(viewportW - padding * 2) / bbox.width,
					(viewportH - padding * 2) / bbox.height
				)
			)
		);

		const targetX = -bbox.x * scale + (viewportW - bbox.width * scale) / 2;
		const targetY = -bbox.y * scale + (viewportH - bbox.height * scale) / 2;

		return {
			scale,
			position: {
				x: targetX,
				y: targetY,
			}
		};
	}

	withOutput(cfg: Konva.ContainerConfig) {
		this.outputCfg = { ...this.outputCfg, ...cfg };
		return this;
	}

	getFullImage(containerCfg?: Konva.ContainerConfig) {
		if (this.attributes?.houseBoundingBox) this.recomputeEdgeLengthVisibility();
		const labels = this.findLabels();
		const solarLinerFtNodes = this.findSolarLinrFeetTextNodes();
		const unselectedPanles = this.findUnselectedPanels();
		const specialEdgesGroup = this.findSpecialEdgesGroup();

		const labelVisibisilty = labels.length > 0 ? labels[0].visible() : false;
		const solarLinerFtNodesVisibility = solarLinerFtNodes.length > 0 ? solarLinerFtNodes[0].visible() : false;
		try {
			labels?.forEach(l => l.visible(false));
			solarLinerFtNodes?.forEach(sN => sN.visible(false));
			unselectedPanles?.forEach(p => p?.visible(false));
			specialEdgesGroup?.visible(false);
			this.findTreeDrawerGroup()?.visible(false);
			this.findFlattenAreaDrawerGroup()?.visible(false);
			this.findDsmEditsFlattenAreaGroup()?.visible(false);
			this.findFlattenAreaFillGroup()?.visible(false);
			this.outputs.baseImage = this.attributes.stage.toDataURL(containerCfg ?? this.outputCfg);
		} finally {
			labels?.forEach((l) => l.visible(labelVisibisilty));
			solarLinerFtNodes?.forEach(sN => sN.visible(solarLinerFtNodesVisibility));
			// unselectedPanles?.forEach(p => p?.visible(true));

		}
		return this;
	}

	getLabelImage(containerCfg?: Konva.ContainerConfig) {
		const scratch = new Konva.Layer({ listening: false, visible: true });
		this.attributes.stage.add(scratch);
		const labels = this.findLabels();
		// console.log('lables', labels);
		try {
			labels.forEach(l => {
				const outline = this.copyNodeToLayer(l, scratch);
				outline.fill('transparent');
				outline.stroke('#000');
				outline.strokeWidth(0.5);
				outline.strokeScaleEnabled(false);

				const text = this.copyNodeToLayer(l, scratch);
				text.fill('#fff');
				scratch.add(outline);
				scratch.add(text);
			});
			this.outputs.labelsOnly = scratch.toDataURL(containerCfg || this.outputCfg);
		} finally {
			scratch.destroy();
		}
		return this;
	}

	getLabelImageOfFilteredRoofSegments(containerCfg?: Konva.ContainerConfig) {
		if(!this.attributes.filteredRoofIds) throw new Error('filtered Roof ids not passed');
		const scratch = new Konva.Layer({ listening: false, visible: true });
		this.attributes.stage.add(scratch);
		const labels = this.findLabels().filter(n => {
			const m = n.getAttr('meta');
			return this.attributes.filteredRoofIds?.includes(m.roofSegId);
		});

		try {
			labels.forEach(l => {
				const outline = this.copyNodeToLayer(l, scratch);
				outline.fill('transparent');
				outline.stroke('#000');
				outline.strokeWidth(0.5);
				outline.strokeScaleEnabled(false);

				const text = this.copyNodeToLayer(l, scratch);
				text.fill('#fff');
				scratch.add(outline);
				scratch.add(text);
			});
			this.outputs.filteredLabelsOnly = scratch.toDataURL(containerCfg || this.outputCfg);
		} finally {
			scratch.destroy();
		}
		return this;
	}

	getSolarLinrFeetNodeImage(containerCfg?: Konva.ContainerConfig) {
		const scratch = new Konva.Layer({ listening: false, visible: true });
		this.attributes.stage.add(scratch);
		const solarLinerFtNodes = this.findSolarLinrFeetTextNodes();
		try {
			solarLinerFtNodes.forEach(
				l => {
					const c = this.copyNodeToLayer(l, scratch, l.getAttr('visibleLengthCondition'));
					scratch.add(c);
				}
			);
			this.outputs.solarLinerFtOnly = scratch.toDataURL(containerCfg || this.outputCfg);
		} finally {
			scratch.destroy();
		}
		return this;
	}

	// to recompute solarLineerText as we move to a new viewPort in the constructor
	private recomputeEdgeLengthVisibility(): void {
		const zoomScale = this.attributes.stage.getAbsoluteScale().x;

		const visibleLengthThreshold =
    (this.attributes.fullScreenEnabled ? 6 : 4) / this.attributes.groundMetersPerPixel;

		const nodes = this.findSolarLinrFeetTextNodes();
		nodes.forEach((n) => {
			const pxLength: number | undefined = n.getAttr('pxLength');
			if(!pxLength) return;
			const scaledLength = pxLength * zoomScale;
			const cond = scaledLength > visibleLengthThreshold;
			n.fontSize(5.5);
			n.setAttr('visibleLengthCondition', cond);
			n.visible(cond);
		});
	}

	getSelectedPanelsImage(containerCfg?: Konva.ContainerConfig) {
		const scratch = new Konva.Layer({ listening: false, visible: true });
		this.attributes.stage.add(scratch);

		const panelsNodes = this.findSelectedPanels() as Konva.Node[];
		try {
			panelsNodes.forEach(l => {
				const c = this.copyNodeToLayer(l, scratch);
				scratch.add(c);
			});
			this.outputs.panelsOnly = scratch.toDataURL(containerCfg || this.outputCfg);
		} finally {
			scratch.destroy();
		}
		return this;
	}

	result() {
		this.attributes.stage.destroy();
		return this.outputs;
	}

	// for new layer transform would be equal to stages
	// and nodes getAbsoluteTransform already has stages tranform peoprs
	// that is why v need to inverse stages tranform first
	copyNodeToLayer(node: Konva.Node, layer: Konva.Layer, visiblity?: boolean) {

		const clone = node.clone({ visible: visiblity ?? true, listening: false });
		// 2) WORLD/ABSOLUTE transform of the source node.
		//    This already includes Stage zoom/pan and every ancestor (groups/layer).
		const nodeAbs = node.getAbsoluteTransform().copy();

		// 3) Convert that world transform into LOCAL coords of the *target parent*.
		//    Here you’re using the Stage as the parent of the new layer. 
		//    Formula: local = parent^{-1} * world
		//
		//    Why invert? Because at render time Konva will do:
		//       final = parent * local
		//    so using local = parent^{-1} * world gives:
		//       final = parent * (parent^{-1} * world) = world  ← same on-screen look
		//
		//    This preserves the Stage zoom exactly once (no “double zoom”).

		const local = layer.getAbsoluteTransform().copy().invert().multiply(nodeAbs);
		const { x, y, rotation, scaleX, scaleY, skewX, skewY, } = local.decompose();

		// 3) apply only the needed transform attrs; avoid double offsets
		clone.setAttrs({
			x, y, rotation, scaleX, scaleY, skewX, skewY,
			offsetX: 0, offsetY: 0, // do we dont double apply it
		});

		return clone;
	}

	// helper for debging
	private dump(node: Konva.Node, tag: string) {
		const abs = node.getAbsoluteTransform().decompose();
		console.log(tag, {
			x: node.x(), y: node.y(),
			rotation: node.rotation(),
			scaleX: node.scaleX(), scaleY: node.scaleY(),
			offsetX: node.offsetX(), offsetY: node.offsetY(),
			abs, // { x,y,rotation,scaleX,scaleY,skewX,skewY } in world space
		});
	}

	private findLabels() {
		return this.attributes.stage.find(this.labelSelector) as Konva.Text[];
	}

	private findSolarLinrFeetTextNodes() {
		return this.attributes.stage.find(this.solarLinerFtSelector) as Konva.Text[];
	}

	private findUnselectedPanels() {
		const panelObjs = (this.attributes.stage.find(this.panelsSelector) as Konva.Group[]).map(g => g.children).flat();
		return panelObjs.filter(p => p?.attrs['fill'] === PANEL_OFF_COLOR);
	}

	private findSelectedPanels() {
		const panelObjs = (this.attributes.stage.find(this.panelsSelector) as Konva.Group[]).map(g => g.children).flat();
		return panelObjs.filter(p => p?.attrs['fill'] !== PANEL_OFF_COLOR);
	}

	private findSpecialEdgesGroup(){
		return this.attributes.stage.findOne(this.specialEdgesSelector) as Konva.Group;
	}

	private findTreeDrawerGroup(){
		return this.attributes.stage.findOne(this.treeDrawerSelector) as Konva.Group;
	}

	private findFlattenAreaDrawerGroup(){
		return this.attributes.stage.findOne(this.flattenAreaDrawerSelector) as Konva.Group;
	}

	private findDsmEditsFlattenAreaGroup(){
		return this.attributes.stage.findOne(this.dsmEditsFlattenAreaSelector) as Konva.Group;
	}

	private findFlattenAreaFillGroup(){
		return this.attributes.stage.findOne(this.flattenAreaFillSelector) as Konva.Group;
	}
}

export {
	KonvaGroupNames,
	getExistingPanelsGroupID,
	getExistingFacetsGroupID,
	getRoofSegmentIDFromGroupID,
	getObsIDFromGroupID,
	getInProgressFacetsGroupID,
	getExistingFacetDisplayName,
	findKonvaShapeByID,
	KonvaClassNames,
	KonvaLayerIds,
	RoofImageBuilder,
	konvaImageIds,
	checkIfAnyDrawnFacetExists,
	checkIfAnyDrawnObstructionExists,
	getBaseRasterImage,
	isBaseRasterImageLoaded,
	waitForBaseRasterImage,
	notifyBaseRasterImageLoaded
};

